const positiveInteger = (value) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};
const appointmentStart = (row) => row?.showstartdate || row?.start_datetime || "";
const appointmentEnd = (row) => row?.showenddate || row?.end_datetime || "";

export const normalizeResourceAllocations = (appointment = {}) => {
  const allocations = Array.isArray(appointment.resource_allocations)
    ? appointment.resource_allocations
    : (Array.isArray(appointment.resourceAllocations) ? appointment.resourceAllocations : []);
  if (allocations.length) return allocations.map((row) => ({
    ...row,
    requirement_id: positiveInteger(row.requirement_id ?? row.service_resource_requirement_id),
    resource_id: positiveInteger(row.resource_id),
    resource_item_id: positiveInteger(row.resource_item_id),
    duration_minutes: positiveInteger(row.duration_minutes ?? row.hold_duration_minutes),
    hold_type: row.hold_type === "partial" ? "partial" : "whole",
    resource_name: row.resource_name || row.resource?.resource_name || "",
    resource_item_name: row.resource_item_name || row.resource_item?.resource_item || row.resource_item?.name || "",
    hold_start_datetime: row.hold_start_datetime || appointmentStart(appointment),
    hold_end_datetime: row.hold_end_datetime || appointmentEnd(appointment),
  }));
  const legacyId = positiveInteger(appointment.resource_item_id ?? appointment.resource_item?.id);
  if (!legacyId) return [];
  return [{
    requirement_id: null,
    resource_id: positiveInteger(appointment.resource_id ?? appointment.resource_item?.resource_id),
    resource_item_id: legacyId,
    resource_name: appointment.resource_name || "",
    resource_item_name: appointment.resource_item?.resource_item || appointment.resource_item?.name || "",
    hold_type: "whole",
    duration_minutes: null,
    hold_start_datetime: appointmentStart(appointment),
    hold_end_datetime: appointmentEnd(appointment),
    legacy: true,
  }];
};

export const bookingResourceChoices = (requirements, choices = {}) => (Array.isArray(requirements) ? requirements : [])
  .map((requirement) => ({ requirement_id: positiveInteger(requirement.id ?? requirement.requirement_id), resource_item_id: positiveInteger(choices[requirement.id ?? requirement.requirement_id]) }))
  .filter((row) => row.requirement_id && row.resource_item_id);

export const resourceAllocationChoices = (allocations) => (Array.isArray(allocations) ? allocations : [])
  .map((row) => ({ requirement_id: positiveInteger(row.requirement_id ?? row.service_resource_requirement_id), resource_item_id: positiveInteger(row.resource_item_id) }))
  .filter((row) => row.requirement_id && row.resource_item_id);

export const hasSavedResourceSelection = (appointment) =>
  Boolean(positiveInteger(appointment?.resource_item_id))
  || resourceAllocationChoices(appointment?.resource_allocations).length > 0;

const itemOption = (item) => ({
  value: positiveInteger(item?.value ?? item?.id),
  label: item?.label || item?.resource_item || item?.name || "Resource item",
});

export const normalizeRequirementOptions = (response, fallbackRequirements = []) => {
  const grouped = Array.isArray(response?.resource_requirements)
    ? response.resource_requirements
    : (Array.isArray(response?.requirements) ? response.requirements : []);
  if (grouped.length) return grouped.map((row) => ({
    requirement_id: positiveInteger(row.id ?? row.requirement_id),
    resource_id: positiveInteger(row.resource_id),
    label: row.resource_name || row.resource?.resource_name || "Resource",
    ...(row.unavailable_reason ? { unavailable_reason: row.unavailable_reason } : {}),
    options: (Array.isArray(row.resource_items) ? row.resource_items : Array.isArray(row.options) ? row.options : [])
      .map(itemOption).filter((item) => item.value),
  })).filter((row) => row.requirement_id);
  const requirements = Array.isArray(fallbackRequirements) ? fallbackRequirements : [];
  if (!requirements.length) return [];
  const flat = Array.isArray(response) ? response : [];
  return requirements.map((row, index) => ({
    requirement_id: positiveInteger(row.id ?? row.requirement_id),
    resource_id: positiveInteger(row.resource_id),
    label: row.resource_name || row.resource?.resource_name || `Resource ${index + 1}`,
    options: (index === 0 ? flat : (Array.isArray(row.resource_items) ? row.resource_items : []))
      .map(itemOption).filter((item) => item.value),
  })).filter((row) => row.requirement_id);
};

export const requirementOptionsWithSavedAllocations = (response, allocations = []) => {
  const groups = normalizeRequirementOptions(response);
  const saved = normalizeResourceAllocations({ resource_allocations: allocations })
    .filter((row) => row.requirement_id && row.resource_item_id);
  return saved.reduce((result, row) => {
    const index = result.findIndex((group) => group.requirement_id === row.requirement_id);
    if (index >= 0 && result[index].unavailable_reason) return result;
    const option = { value: row.resource_item_id, label: row.resource_item_name || `Resource item ${row.resource_item_id}` };
    if (index < 0) {
      result.push({ requirement_id: row.requirement_id, resource_id: row.resource_id, label: row.resource_name || "Resource", options: [option] });
    } else if (!result[index].options.some((item) => item.value === row.resource_item_id)) {
      result[index] = { ...result[index], options: [...result[index].options, option] };
    }
    return result;
  }, [...groups]);
};

export const resourceRequirementProblem = (group) => {
  if (group.unavailable_reason === "inactive_resource") return `${group.label} is inactive. Update the service's resource requirements before booking.`;
  if (group.unavailable_reason === "missing_resource") return `${group.label} is missing. Update the service's resource requirements before booking.`;
  return "";
};

export const matchResourceItemToRequirement = (response, resourceItemId) => {
  const itemId = positiveInteger(resourceItemId);
  if (!itemId) return [];
  const group = normalizeRequirementOptions(response).find((row) =>
    row.options.some((option) => option.value === itemId));
  return group ? [{ requirement_id: group.requirement_id, resource_item_id: itemId }] : [];
};

export const hasEveryRequiredResource = (response, allocations) => {
  const groups = normalizeRequirementOptions(response);
  if (!groups.length) return true;
  const choices = resourceAllocationChoices(allocations);
  return groups.every((group) => choices.some((choice) =>
    choice.requirement_id === group.requirement_id
    && group.options.some((option) => option.value === choice.resource_item_id)));
};

export const withResourceAllocationPayload = (values = {}, allocations) => {
  const normalized = resourceAllocationChoices(allocations);
  return { ...values, resource_allocations: normalized, resource_item_id: normalized[0]?.resource_item_id || "" };
};

export const resourceLaneEvents = (appointment, suppliedAllocations, optionType) => {
  if (optionType !== "resource" && optionType !== "resourceItem") return [{
    resourceId: appointment?.staff?.id ?? appointment?.staff_id,
    start: appointmentStart(appointment), end: appointmentEnd(appointment), allocation: null,
  }];
  const allocations = Array.isArray(suppliedAllocations) ? suppliedAllocations : normalizeResourceAllocations(appointment);
  return allocations.map((allocation) => ({
    resourceId: allocation.resource_item_id,
    start: allocation.hold_start_datetime || appointmentStart(appointment),
    end: allocation.hold_end_datetime || appointmentEnd(appointment),
    allocation,
  }));
};
